import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import styles from './AddAutoReplyWindow.module.css';
import { useRootStore } from '../../../../../mobx/bridge';
import * as utils from '../../../../../helpers/utils';
import PopperWrapper from '../../PopperWrapper/PopperWrapper';
import AutoReplyRecurringStatus from '../AutoReplyRecurringStatus/AutoReplyRecurringStatus';

const titleCharLimit = 100;
const messageCharLimit = 300;

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const AddAutoReplyWindow = observer(function AddAutoReplyWindow(props) {
  const { t } = useTranslation();
  const { sendingEvents, autoReplyV2Store } = useRootStore();

  const { title, data, button, onClose, arrowDirection, autoReplyType } = props;

  const [awayMessageTitle, setAwayMessageTitle] = React.useState(data?.Title || '');
  const [awayMessageText, setAwayMessageText] = React.useState(data?.Message || '');

  const [recurringStatus, setRecurringStatus] = React.useState(data?.RecurringStatus || null);

  const [showTitleErrorMessage, setShowTitleErrorMessage] = React.useState(false);
  const [showTextErrorMessage, setShowTextErrorMessage] = React.useState(false);
  const [showDaysErrorMessage, setShowDaysErrorMessage] = React.useState(false);
  const [showConflictErrorMessage, setShowConflictErrorMessage] = React.useState('');

  const isAddAutoReplyOpen = Boolean(button);

  const submitAutoReply = debounce(() => {
    const invalidRecurringStatus =
      autoReplyType.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId &&
      recurringStatus === null;

    const conflictingTextAutoReplies =
      data?.IsActiveForTextChat &&
      data.AutoReplyType.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId
        ? autoReplyV2Store.computedGetConflictingScheduledAutoReplies(
            {
              ...data,
              RecurringStatus: recurringStatus,
            },
            utils.MessageTypeEnum.SMS.TypeId
          )
        : [];

    const conflictingWebAutoReplies =
      data?.IsActiveForWebChat &&
      data.AutoReplyType.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId
        ? autoReplyV2Store.computedGetConflictingScheduledAutoReplies(
            {
              ...data,
              RecurringStatus: recurringStatus,
            },
            utils.MessageTypeEnum.DirectMessage.TypeId
          )
        : [];

    const hasConflictingAutoReplies =
      conflictingTextAutoReplies?.length || conflictingWebAutoReplies?.length;

    if (
      !awayMessageTitle?.trim() ||
      !awayMessageText?.trim() ||
      invalidRecurringStatus ||
      hasConflictingAutoReplies
    ) {
      if (!awayMessageTitle?.trim()) {
        setShowTitleErrorMessage(true);
      }

      if (!awayMessageText?.trim()) {
        setShowTextErrorMessage(true);
      }

      if (invalidRecurringStatus) {
        setShowDaysErrorMessage(true);
      }

      if (hasConflictingAutoReplies) {
        setShowConflictErrorMessage(
          conflictingTextAutoReplies
            .concat(conflictingWebAutoReplies)
            .map((m) => m.Title)
            .join(', ')
        );
      }

      return;
    }

    const awayMessage = {
      _id: data?._id || utils.generateGuid(),
      Title: awayMessageTitle,
      Message: awayMessageText,
      AutoReplyType: autoReplyType,
      RecurringStatus: recurringStatus,
    };

    sendingEvents.socketEmitUpsertAutoReply(awayMessage);
    onClose();
  }, 250);

  const onAwayMessageTitleChange = (e) => {
    if (showTitleErrorMessage) {
      setShowTitleErrorMessage(false);
    }
    setAwayMessageTitle(e.target.value.substring(0, titleCharLimit));
  };

  const onAwayMessageTextChange = (e) => {
    if (showTextErrorMessage) {
      setShowTextErrorMessage(false);
    }
    setAwayMessageText(e.target.value.substring(0, messageCharLimit));
  };

  return (
    <PopperWrapper
      isOpen={isAddAutoReplyOpen}
      anchorEl={button}
      arrow
      arrowDirection={arrowDirection}
      onClose={onClose}
    >
      <div className={styles.addAutoReplyWindow}>
        <span className={styles.addAutoReplyTitle}>{title}</span>
        {autoReplyType.TypeId === utils.AutoReplyTypeEnum.ScheduledAutoReply.TypeId ? (
          <AutoReplyRecurringStatus
            onRecurringStatusChange={setRecurringStatus}
            showDaysErrorMessage={showDaysErrorMessage}
            initialRecurringStatus={data?.RecurringStatus}
          />
        ) : null}
        <span className={styles.label}>{t('CustomAwayMessages.Title')}</span>
        <input
          type="text"
          className={styles.textArea}
          value={awayMessageTitle}
          onChange={onAwayMessageTitleChange}
          required
        />
        <span className={styles.errorLabel}>
          {showTitleErrorMessage ? t('AddAutoReplyWindow.TitleCannotBeEmptyError') : ''}
        </span>
        <span
          className={[
            styles.charCountLabel,
            awayMessageTitle?.length >= titleCharLimit ? styles.charCountLabelRed : '',
          ].join(' ')}
        >
          {awayMessageTitle?.length || 0}/{titleCharLimit}
        </span>
        <span className={styles.label}>{t('CustomAwayMessages.Message')}</span>
        <textarea
          className={[styles.textArea, styles.messageArea].join(' ')}
          value={awayMessageText}
          onChange={onAwayMessageTextChange}
          required
        />
        <span className={styles.errorLabel}>
          {showTextErrorMessage ? t('AddAutoReplyWindow.MessageCannotBeEmptyError') : ''}
        </span>
        <span
          className={[
            styles.charCountLabel,
            awayMessageText?.length >= messageCharLimit ? styles.charCountLabelRed : '',
          ].join(' ')}
        >
          {awayMessageText?.length || 0}/{messageCharLimit}
        </span>
        <span className={styles.errorLabel}>
          {showConflictErrorMessage
            ? `${t(
                'AutoReplyWarningMessage.ScheduledAutoReplyConflict1'
              )} ${showConflictErrorMessage}. ${t(
                'AutoReplyWarningMessage.ScheduledAutoReplyConflict2'
              )}`
            : ''}
        </span>
        <div>
          <button
            type="button"
            className={[styles.btn, styles.saveBtn].join(' ')}
            onClick={submitAutoReply}
          >
            {t('Common.Save')}
          </button>
          <button
            type="button"
            className={[styles.btn, styles.cancelBtn].join(' ')}
            onClick={onClose}
          >
            {t('Common.Cancel')}
          </button>
        </div>
      </div>
    </PopperWrapper>
  );
});
AddAutoReplyWindow.whyDidYouRender = true;
AddAutoReplyWindow.displayName = 'AddAutoReplyWindow';

export default React.memo(AddAutoReplyWindow);
